/**
 * React Query hooks for Club Members
 * 
 * Handles member management, roles, and permissions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membersApi, type UpdateMemberRolePayload } from '../lib/api/members';

/**
 * Fetch all members of a club
 * 
 * Query Key: ['clubMembers', clubSlug]
 * Returns: Array of club members with their roles
 */
export function useClubMembers(clubSlug: string) {
  return useQuery({
    queryKey: ['clubMembers', clubSlug],
    queryFn: () => membersApi.getClubMembers(clubSlug),
    enabled: !!clubSlug,
    staleTime: 1000 * 60, // 1 minute - member lists change occasionally
  });
}

/**
 * Update a member's role
 * 
 * On success: Invalidates ['clubMembers', clubSlug] to refresh the list
 */
export function useUpdateMemberRole(clubSlug: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UpdateMemberRolePayload['role'] }) =>
      membersApi.updateMemberRole(clubSlug, userId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubMembers', clubSlug] });
      queryClient.invalidateQueries({ queryKey: ['club', clubSlug] });
    },
  });
}

/**
 * Remove a member from the club
 * 
 * On success: Invalidates ['clubMembers', clubSlug] to refresh the list
 */
export function useRemoveMember(clubSlug: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => membersApi.removeMember(clubSlug, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubMembers', clubSlug] });
      queryClient.invalidateQueries({ queryKey: ['club', clubSlug] }); // Update member count
    },
  });
}
